/**
 * Investor track record — read/write against `investor_track_record`.
 *
 * Track record is "companies I have invested in", captured during onboarding
 * and shown on the investor profile as credibility. It is NOT the portfolio:
 * holdings (current positions with valuations and returns) live in
 * `portfolio_companies`, are entitlement-gated to Standard+, and are handled by
 * `investor-queries.ts`. The two were conflated in one local list, which is why
 * fixing one never fixed the symptom — see
 * supabase/migrations/20260731100000_investor_track_record.sql for the full
 * account.
 *
 * IDENTITY: every function here takes the AUTH USER id (`user.id`), never
 * `investor_profiles.id`. The table's RLS is `investor_user_id = auth.uid()`.
 * Passing the profile id writes rows the owner cannot read back — the exact bug
 * that made every portfolio write fail silently in production before
 * 2026-07-30.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { InvestmentRecord } from '@/lib/state/profile-store';
import type { AuthUserId } from '@/lib/ids';

import { queryKeys } from './api';
import { isSupabaseConfigured, supabase } from './client';
import type { Database } from './database.types';

type TrackRecordRow = Database['public']['Tables']['investor_track_record']['Row'];
type TrackRecordInsert = Database['public']['Tables']['investor_track_record']['Insert'];

/**
 * The columns that identify a record. Must match the unique index
 * `investor_track_record_identity_idx`, or the upsert silently becomes an
 * insert and duplicates rows.
 *
 * `company_name_key` is GENERATED in Postgres — it is named here because
 * ON CONFLICT must reference the indexed columns, but it is never written.
 */
const IDENTITY_CONFLICT_TARGET =
  'investor_user_id,company_name_key,investment_date,stage';

/**
 * The client-side mirror of `investor_track_record_identity_idx`.
 *
 * Used to dedupe a batch before it reaches Postgres and to work out which
 * local records are missing from the server. Must stay in step with the index
 * — if it drifts, reconciliation re-uploads rows that already exist and the
 * upsert quietly turns into an update-storm rather than a no-op.
 */
export function trackRecordIdentityKey(record: InvestmentRecord): string {
  return [record.companyName.trim().toLowerCase(), record.date, record.stage].join(' ');
}

// ---------------------------------------------------------------------------
// Shape mapping
// ---------------------------------------------------------------------------

/**
 * DB row → the `InvestmentRecord` shape the UI already speaks.
 *
 * Holdings-only fields (`currentValuation`, `ownership`, `status`, `metrics`,
 * `lastUpdate`) are deliberately absent: they belong to `portfolio_companies`.
 * Leaving them undefined here is what keeps the two concepts from re-merging.
 */
export function trackRecordRowToInvestmentRecord(row: TrackRecordRow): InvestmentRecord {
  return {
    id: row.id,
    companyName: row.company_name,
    industry: row.industry ?? undefined,
    stage: row.stage as InvestmentRecord['stage'],
    investmentType: row.investment_type as InvestmentRecord['investmentType'],
    securityType: (row.security_type ?? undefined) as InvestmentRecord['securityType'],
    date: row.investment_date,
    investmentAmount: row.investment_amount ?? undefined,
    valuationAtInvestment: row.valuation_at_investment ?? undefined,
    investorRole: (row.investor_role ?? undefined) as InvestmentRecord['investorRole'],
    source: row.source as InvestmentRecord['source'],
  };
}

/**
 * `InvestmentRecord` → an insert payload.
 *
 * The local `id` is NOT carried over: local ids are `Date.now().toString()`,
 * minted per device, so the same investment entered on a phone and on the web
 * has two different ids. Identity is the natural key instead, which is what
 * makes the multi-device backfill converge on one row per investment.
 */
export function investmentRecordToInsert(
  userId: string,
  record: InvestmentRecord,
): TrackRecordInsert {
  return {
    investor_user_id: userId,
    company_name: record.companyName.trim(),
    industry: record.industry ?? null,
    stage: record.stage,
    investment_type: record.investmentType,
    security_type: record.securityType ?? null,
    investment_date: record.date,
    investment_amount: record.investmentAmount ?? null,
    valuation_at_investment: record.valuationAtInvestment ?? null,
    investor_role: record.investorRole ?? null,
    source: record.source,
  };
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

/** Fetch the signed-in investor's track record. Takes the AUTH user id. */
export function useTrackRecord(userId: AuthUserId | undefined) {
  return useQuery({
    queryKey: queryKeys.trackRecord(userId ?? ''),
    queryFn: async (): Promise<InvestmentRecord[]> => {
      if (!userId || !isSupabaseConfigured()) return [];

      const { data, error } = await supabase
        .from('investor_track_record')
        .select('*')
        .eq('investor_user_id', userId)
        .order('investment_date', { ascending: false });

      if (error) throw error;
      return (data ?? []).map(trackRecordRowToInvestmentRecord);
    },
    enabled: !!userId && isSupabaseConfigured(),
  });
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/**
 * Insert or update one track-record entry.
 *
 * Upsert rather than insert so a double-submit converges instead of
 * duplicating — the check-then-insert shape it avoids is what produced
 * duplicate `finai` / `finai-2` directory rows in production.
 */
export function useUpsertTrackRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { userId: string; record: InvestmentRecord }) => {
      if (!isSupabaseConfigured()) throw new Error('Supabase not configured');

      const { data, error } = await supabase
        .from('investor_track_record')
        .upsert(investmentRecordToInsert(vars.userId, vars.record), {
          onConflict: IDENTITY_CONFLICT_TARGET,
        })
        .select()
        .single();

      if (error) throw error;
      return trackRecordRowToInvestmentRecord(data);
    },
    onSuccess: (_result, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trackRecord(vars.userId) });
    },
  });
}

/** Delete one track-record entry by its server id. */
export function useDeleteTrackRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { userId: string; id: string }) => {
      if (!isSupabaseConfigured()) throw new Error('Supabase not configured');

      const { error } = await supabase
        .from('investor_track_record')
        .delete()
        .eq('id', vars.id)
        .eq('investor_user_id', vars.userId);

      if (error) throw error;
    },
    onSuccess: (_result, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trackRecord(vars.userId) });
    },
  });
}

/**
 * Upload a batch of records, returning the number written.
 *
 * Used by onboarding (save the step's list) and by the one-time backfill
 * (`syncTrackRecordUp`). One request, not N — onboarding lists are small but a
 * per-row round trip on a phone network is where partial writes come from.
 *
 * THROWS on failure. Callers must surface it: the whole reason this table
 * exists is that the original write was fire-and-forget with `silent: true`,
 * so an investor's history vanished with no signal at all.
 */
export async function upsertTrackRecords(
  userId: string,
  records: InvestmentRecord[],
): Promise<number> {
  if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
  if (records.length === 0) return 0;

  // Two entries in one batch can collide on the natural key (same company,
  // year and stage typed twice). Postgres rejects a whole ON CONFLICT batch
  // that contains a duplicate — "cannot affect row a second time" — so the
  // batch is deduped here first, keeping the last occurrence.
  const deduped = [...
    records
      .reduce((acc, record) => {
        acc.set(trackRecordIdentityKey(record), record);
        return acc;
      }, new Map<string, InvestmentRecord>())
      .values()
  ];

  const { error } = await supabase
    .from('investor_track_record')
    .upsert(
      deduped.map((record) => investmentRecordToInsert(userId, record)),
      { onConflict: IDENTITY_CONFLICT_TARGET },
    );

  if (error) throw error;
  return deduped.length;
}
